/**
 * Dashboard (/): drei Live-KPI-Karten (Anwesenheit heute, laufende
 * Abwesenheiten nach Grund, Kopfzahlen) + die Fach-Widgets aus EINEM
 * /dashboard-Call (faellige Schulungen, faellige Vorsorge, ablaufende
 * Beauftragungen, Monats-Stunden/-Zulagen).
 */

import { useMemo, type ReactNode } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  BadgeEuro,
  CalendarOff,
  ClipboardSignature,
  GraduationCap,
  Stethoscope,
  Users,
} from "lucide-react"
import { Link } from "react-router-dom"
import { EXAMINATION_STATUS } from "@/components/examinations/labels"
import { MITARBEITERTYP_LABEL } from "@/components/employees/labels"
import { DueBadge, LoadingSpinner } from "@/components/shared"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ABSENCE_REASONS, ABSENCE_REASON_VALUES } from "@/lib/absenceColors"
import { fmtDate, fmtEuro, fmtHours } from "@/lib/format"
import { todayIso } from "@/lib/isoWeek"
import { useCan } from "@/lib/permissions"
import { cn } from "@/lib/utils"
import { api } from "@/services/api"
import type { AbwesenheitGrund, Mitarbeitertyp } from "@/types"

/** Fach-Widget: Zaehler + Top-Liste + „alle anzeigen"-Link */
function DueListCard({
  icon: Icon,
  title,
  count,
  linkTo,
  linkLabel,
  isLoading,
  children,
}: {
  icon: typeof GraduationCap
  title: string
  count: number | undefined
  linkTo: string
  linkLabel: string
  isLoading: boolean
  children: ReactNode
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Icon className="size-4 text-muted-foreground" /> {title}
          </span>
          {count !== undefined && (
            <span className="text-2xl font-semibold tabular-nums">{count}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? <LoadingSpinner className="h-6 w-6" /> : children}
        <Link
          to={linkTo}
          className="block text-xs text-primary underline-offset-2 hover:underline"
        >
          {linkLabel}
        </Link>
      </CardContent>
    </Card>
  )
}

export function DashboardPage() {
  const canBoard = useCan("board_nutzen")
  const today = useMemo(todayIso, [])

  const board = useQuery({
    queryKey: ["attendance", "board", today],
    queryFn: () => api.attendance.board(today),
    enabled: canBoard,
  })

  const absences = useQuery({
    queryKey: ["absences", { from: today, to: today, size: 100 }],
    queryFn: () => api.absences.list({ from: today, to: today, size: 100 }),
  })

  // EIN Call fuer alle Fach-Widgets (PRD §6.3)
  const dash = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.dashboard.get(),
  })

  // Laufende Abwesenheiten nach Grund (heute), fuer die kleine Farbleiste
  const byReason = useMemo(() => {
    const counts = new Map<AbwesenheitGrund, number>()
    for (const a of absences.data?.data ?? []) {
      counts.set(a.reason, (counts.get(a.reason) ?? 0) + 1)
    }
    return ABSENCE_REASON_VALUES.filter((r) => counts.has(r)).map((r) => ({
      reason: r,
      count: counts.get(r)!,
    }))
  }, [absences.data])
  const absenceTotal = byReason.reduce((sum, r) => sum + r.count, 0)

  const headcount = dash.data?.headcount

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <span className="text-sm text-muted-foreground">{fmtDate(today)}</span>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {/* KPI 1: Anwesenheit heute */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="size-4 text-muted-foreground" /> Anwesenheit heute
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!canBoard ? (
              <p className="text-sm text-muted-foreground">Keine Board-Berechtigung.</p>
            ) : board.isLoading ? (
              <LoadingSpinner className="h-6 w-6" />
            ) : board.data ? (
              <div className="flex items-end gap-6">
                <div>
                  <p className="text-3xl font-semibold tabular-nums">
                    {board.data.checkedIn.length}
                  </p>
                  <p className="text-xs text-muted-foreground">eingebucht</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold tabular-nums">
                    {board.data.absent.length}
                  </p>
                  <p className="text-xs text-muted-foreground">abwesend</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold tabular-nums">
                    {board.data.checkedOut.length}
                  </p>
                  <p className="text-xs text-muted-foreground">ausgebucht</p>
                </div>
                {board.data.previousDayOpen.length > 0 && (
                  <p className="pb-1 text-xs text-amber-700">
                    +{board.data.previousDayOpen.length} Vortag offen
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Keine Daten.</p>
            )}
          </CardContent>
        </Card>

        {/* KPI 2: Laufende Abwesenheiten nach Grund */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarOff className="size-4 text-muted-foreground" /> Laufende Abwesenheiten
            </CardTitle>
          </CardHeader>
          <CardContent>
            {absences.isLoading ? (
              <LoadingSpinner className="h-6 w-6" />
            ) : (
              <div className="space-y-3">
                <p className="text-3xl font-semibold tabular-nums">{absenceTotal}</p>
                {absenceTotal > 0 ? (
                  <>
                    {/* Farbleiste: Anteil je Grund */}
                    <div className="flex h-2 w-full overflow-hidden rounded-full">
                      {byReason.map((r) => (
                        <div
                          key={r.reason}
                          className={cn(ABSENCE_REASONS[r.reason].bar)}
                          style={{ width: `${(r.count / absenceTotal) * 100}%` }}
                          title={`${ABSENCE_REASONS[r.reason].label}: ${r.count}`}
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {byReason.map((r) => (
                        <span
                          key={r.reason}
                          className="flex items-center gap-1 text-xs text-muted-foreground"
                        >
                          <span
                            className={cn("size-2 rounded-full", ABSENCE_REASONS[r.reason].bar)}
                          />
                          {ABSENCE_REASONS[r.reason].label} ({r.count})
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Keine laufenden Abwesenheiten heute.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* KPI 3: Kopfzahlen (aktiv/gesamt + nach Mitarbeitertyp) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="size-4 text-muted-foreground" /> Kopfzahlen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dash.isLoading ? (
              <LoadingSpinner className="h-6 w-6" />
            ) : headcount ? (
              <div className="space-y-2">
                <p className="text-3xl font-semibold tabular-nums">{headcount.active}</p>
                <p className="text-xs text-muted-foreground">
                  aktiv von {headcount.total} im Verzeichnis
                </p>
                <div className="space-y-0.5">
                  {Object.entries(headcount.byType).map(([type, count]) => (
                    <p
                      key={type}
                      className="flex justify-between text-xs text-muted-foreground"
                    >
                      <span>{MITARBEITERTYP_LABEL[type as Mitarbeitertyp] ?? type}</span>
                      <span className="tabular-nums">{count}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Keine Daten.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fach-Widgets aus dem /dashboard-Call */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {/* Fällige Schulungen */}
        <DueListCard
          icon={GraduationCap}
          title="Fällige Schulungen"
          count={dash.data?.dueTrainings.count}
          linkTo="/schulungen"
          linkLabel="Alle Fälligkeiten anzeigen"
          isLoading={dash.isLoading}
        >
          {dash.data && dash.data.dueTrainings.top.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nichts fällig — alles im grünen Bereich.</p>
          ) : (
            <ul className="space-y-1">
              {dash.data?.dueTrainings.top.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">
                    <span className="font-medium">{t.employee_name}</span>{" "}
                    <span className="text-muted-foreground">· {t.course_name}</span>
                  </span>
                  <DueBadge date={t.expiry_date} />
                </li>
              ))}
            </ul>
          )}
        </DueListCard>

        {/* Fällige Vorsorge */}
        <DueListCard
          icon={Stethoscope}
          title="Fällige Vorsorge"
          count={dash.data?.dueExaminations.count}
          linkTo="/vorsorge"
          linkLabel="Zum Fälligkeits-Cockpit"
          isLoading={dash.isLoading}
        >
          {dash.data && dash.data.dueExaminations.top.length === 0 ? (
            <p className="text-xs text-muted-foreground">Keine fälligen Untersuchungen.</p>
          ) : (
            <ul className="space-y-1">
              {dash.data?.dueExaminations.top.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">
                    <span className="font-medium">{e.employee_name}</span>{" "}
                    <span
                      className="text-muted-foreground"
                      title={EXAMINATION_STATUS[e.status]?.label ?? e.status}
                    >
                      · {e.examination_name}
                    </span>
                  </span>
                  <DueBadge date={e.due_date} />
                </li>
              ))}
            </ul>
          )}
        </DueListCard>

        {/* Ablaufende Beauftragungen */}
        <DueListCard
          icon={ClipboardSignature}
          title="Ablaufende Beauftragungen"
          count={dash.data?.expiringAssignments.count}
          linkTo="/mitarbeiter"
          linkLabel="Zum Mitarbeiterverzeichnis"
          isLoading={dash.isLoading}
        >
          {dash.data && dash.data.expiringAssignments.top.length === 0 ? (
            <p className="text-xs text-muted-foreground">Keine ablaufenden Beauftragungen.</p>
          ) : (
            <ul className="space-y-1">
              {dash.data?.expiringAssignments.top.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">
                    <span className="font-medium">{a.employee_name}</span>{" "}
                    <span className="text-muted-foreground">· {a.type_name}</span>
                  </span>
                  <DueBadge date={a.end_date} />
                </li>
              ))}
            </ul>
          )}
        </DueListCard>

        {/* Laufender Monat: Stunden + Zulagen */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <BadgeEuro className="size-4 text-muted-foreground" /> Laufender Monat
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dash.isLoading ? (
              <LoadingSpinner className="h-6 w-6" />
            ) : dash.data ? (
              <div className="flex items-end gap-6">
                <div>
                  <p className="text-3xl font-semibold tabular-nums">
                    {fmtHours(Number(dash.data.monthHours))}
                  </p>
                  <p className="text-xs text-muted-foreground">Σ Stunden</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold tabular-nums">
                    {fmtEuro(Number(dash.data.monthSurcharges))}
                  </p>
                  <p className="text-xs text-muted-foreground">Σ Zulagen</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Keine Daten.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
